import { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import * as PropTypes from 'prop-types'
import { Megaphone, Info } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import ToggleField from '../FormLegacy/FormFieldsV2/ToggleField'
import { selectUserData } from '../../reducers/userDataReducer'
import API from '../../api/api'
import { useSnackbar } from '../../helpers/hooks/useSnackbar'

const UserNotificationToggleField = ({
  label,
  id,
  name,
  value,
  isAdmin,
  isLoading,
  handleToggleField,
}) => {
  const { t } = useTranslation(['user-panel'])
  return (
    <div className="notification-item" key={id}>
      <div className="notification-item__wrapper">
        <ToggleField
          label={label}
          id={id}
          name={name}
          disabled={isAdmin || isLoading}
          onChange={() => handleToggleField(name)}
          value={value || false}
        />
        <span className={`notification-status ${value ? 'active' : 'inactive'}`}>
          {value ? t('user-panel:notifications.active') : t('user-panel:notifications.inactive')}
        </span>
      </div>
    </div>
  )
}

const UserNotifications = ({ userData }) => {
  const { t } = useTranslation(['user-panel'])
  const { displaySnackbar } = useSnackbar()
  const [notificationsSettings, setNotificationsSettings] = useState(null)
  const [loadingFields, setLoadingFields] = useState(new Set())

  const isAdmin = userData?.user?.role === 'Administrator'
  const isShiftManager = userData?.user?.role === 'ShiftManager'
  const isStaff = userData?.user?.role === 'Staff'

  useEffect(() => {
    if (isShiftManager) {
      API.getShiftManagerNotificationsSettings().then((res) => {
        setNotificationsSettings(res.data)
      })
    } else if (isStaff) {
      API.getStaffNotificationsSettings().then((res) => {
        setNotificationsSettings(res.data)
      })
    } else {
      API.getPropertyManagerNotificationsSettings().then((res) => {
        setNotificationsSettings(res.data)
      })
    }
  }, [])

  const handleToggleField = async (fieldName) => {
    if (loadingFields.has(fieldName)) return

    setLoadingFields((prev) => new Set(prev).add(fieldName))

    const updatedNotificationSettings = {
      ...notificationsSettings,
      [fieldName]: !notificationsSettings[fieldName],
    }

    try {
      if (isShiftManager) {
        await API.updateShiftManagerNotificationsSettings(updatedNotificationSettings)
      } else if (isStaff) {
        await API.updateStaffNotificationsSettings(updatedNotificationSettings)
      } else {
        await API.updatePropertyManagerNotificationsSettings(updatedNotificationSettings)
      }
      setNotificationsSettings(updatedNotificationSettings)
    } catch {
      displaySnackbar(t('user-panel:notifications.updateFailed'), true)
    } finally {
      setLoadingFields((prev) => {
        const newSet = new Set(prev)
        newSet.delete(fieldName)
        return newSet
      })
    }
  }

  if (isShiftManager) {
    return (
      <div className="user-notifications">
        <div className="notification-section">
          <div className="notification-section__header">
            <Megaphone size={20} weight="regular" />
            <span className="notification-section__title">
              {t('user-panel:notifications.generalNotifications')}
            </span>
          </div>
          <div className="notification-section__content">
            <UserNotificationToggleField
              label={t('user-panel:notifications.newGuestRegistration')}
              id="disableNewGuestNotification"
              name="disableNewGuestNotification"
              value={!notificationsSettings?.disableNewGuestNotification}
              isAdmin={isAdmin}
              isLoading={loadingFields.has('disableNewGuestNotification')}
              handleToggleField={handleToggleField}
            />
            <UserNotificationToggleField
              label={t('user-panel:notifications.maintenance')}
              id="disableMaintenanceNotification"
              name="disableMaintenanceNotification"
              value={!notificationsSettings?.disableMaintenanceNotification}
              isAdmin={isAdmin}
              isLoading={loadingFields.has('disableMaintenanceNotification')}
              handleToggleField={handleToggleField}
            />
            <UserNotificationToggleField
              label={t('user-panel:notifications.reservations')}
              id="disableReservationNotification"
              name="disableReservationNotification"
              value={!notificationsSettings?.disableReservationNotification}
              isAdmin={isAdmin}
              isLoading={loadingFields.has('disableReservationNotification')}
              handleToggleField={handleToggleField}
            />
          </div>
        </div>
      </div>
    )
  }

  if (isStaff) {
    const staffToggles = [
      { label: t('user-panel:notifications.mntReqCreation'), name: 'disableMaintenanceCreation' },
      { label: t('user-panel:notifications.mntReqAssign'), name: 'disableMaintenanceAssigned' },
      { label: t('user-panel:notifications.mntReqReminder'), name: 'disableMaintenanceReminder' },
      {
        label: t('user-panel:notifications.unreadChatMessages'),
        name: 'disableChatNotifications',
      },
    ]

    return (
      <div className="user-notifications">
        <div className="notification-section">
          <div className="notification-section__header">
            <Megaphone size={20} weight="regular" />
            <span className="notification-section__title">
              {t('user-panel:notifications.maintenanceNotifications')}
            </span>
          </div>
          <div className="notification-section__content">
            {staffToggles.map(({ label, name }) => (
              <UserNotificationToggleField
                key={name}
                label={label}
                id={name}
                name={name}
                value={!notificationsSettings?.[name]}
                isAdmin={isAdmin}
                isLoading={loadingFields.has(name)}
                handleToggleField={handleToggleField}
              />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="user-notifications">
      {isAdmin && (
        <div className="notification-info-banner">
          <Info size={20} weight="fill" />
          <span>{t('user-panel:notifications.adminBanner')}</span>
        </div>
      )}
      <div className="notification-section">
        <div className="notification-section__header">
          <Megaphone size={20} weight="regular" />
          <span className="notification-section__title">
            {t('user-panel:notifications.generalNotifications')}
          </span>
        </div>
        <div className="notification-section__content">
          <UserNotificationToggleField
            label={t('user-panel:notifications.newClientRegistration')}
            id="newClientsRegistration"
            name="disable_new_client_registration"
            value={!notificationsSettings?.disable_new_client_registration}
            isAdmin={isAdmin}
            isLoading={loadingFields.has('disable_new_client_registration')}
            handleToggleField={handleToggleField}
          />
          <UserNotificationToggleField
            label={t('user-panel:notifications.newGuestRegistration')}
            id="newGuestRegistration"
            name="disable_new_guest_notification"
            value={!notificationsSettings?.disable_new_guest_notification}
            isAdmin={isAdmin}
            isLoading={loadingFields.has('disable_new_guest_notification')}
            handleToggleField={handleToggleField}
          />
          <UserNotificationToggleField
            label={t('user-panel:notifications.newClientApproval')}
            id="newClientApproval"
            name="disable_new_client_approval"
            value={!notificationsSettings?.disable_new_client_approval}
            isAdmin={isAdmin}
            isLoading={loadingFields.has('disable_new_client_approval')}
            handleToggleField={handleToggleField}
          />
          <UserNotificationToggleField
            label={t('user-panel:notifications.reservations')}
            id="reservations"
            name="disable_reservation"
            value={!notificationsSettings?.disable_reservation}
            isAdmin={isAdmin}
            isLoading={loadingFields.has('disable_reservation')}
            handleToggleField={handleToggleField}
          />
          <UserNotificationToggleField
            label={t('user-panel:notifications.appointmentsConfirmations')}
            id="appointmentsConfirmations"
            name="disable_appointment_confirmation"
            value={!notificationsSettings?.disable_appointment_confirmation}
            isAdmin={isAdmin}
            isLoading={loadingFields.has('disable_appointment_confirmation')}
            handleToggleField={handleToggleField}
          />
          <UserNotificationToggleField
            label={t('user-panel:notifications.postApproval')}
            id="postApproval"
            name="disable_post_approval"
            value={!notificationsSettings?.disable_post_approval}
            isAdmin={isAdmin}
            isLoading={loadingFields.has('disable_post_approval')}
            handleToggleField={handleToggleField}
          />
          <UserNotificationToggleField
            label={t('user-panel:notifications.chatNotifications')}
            id="chatNotifications"
            name="disable_chat_notifications"
            value={!notificationsSettings?.disable_chat_notifications}
            isAdmin={isAdmin}
            isLoading={loadingFields.has('disable_chat_notifications')}
            handleToggleField={handleToggleField}
          />
          <UserNotificationToggleField
            label={t('user-panel:notifications.maintenance')}
            id="maintenance"
            name="disable_maintenance"
            value={!notificationsSettings?.disable_maintenance}
            isAdmin={isAdmin}
            isLoading={loadingFields.has('disable_maintenance')}
            handleToggleField={handleToggleField}
          />
          <UserNotificationToggleField
            label={t('user-panel:notifications.surveyEnd')}
            id="disableSurveyEnd"
            name="disable_survey_end"
            value={!notificationsSettings?.disable_survey_end}
            isAdmin={isAdmin}
            isLoading={loadingFields.has('disable_survey_end')}
            handleToggleField={handleToggleField}
          />
          <UserNotificationToggleField
            label={t('user-panel:notifications.surveyResponse')}
            id="disableSurveyResponse"
            name="disable_survey_response"
            value={!notificationsSettings?.disable_survey_response}
            isAdmin={isAdmin}
            isLoading={loadingFields.has('disable_survey_response')}
            handleToggleField={handleToggleField}
          />
          <UserNotificationToggleField
            label={t('user-panel:notifications.saltospaceNotifications')}
            id="disableSaltospaceNotifications"
            name="disable_salto_space_new_client_notification"
            value={!notificationsSettings?.disable_salto_space_new_client_notification}
            isAdmin={isAdmin}
            isLoading={loadingFields.has('disable_salto_space_new_client_notification')}
            handleToggleField={handleToggleField}
          />
          <UserNotificationToggleField
            label={t('user-panel:notifications.iglooCustomPinNotifications')}
            id="disableIglooCustomPinNotifications"
            name="disable_igloowork_custom_pin_notification"
            value={!notificationsSettings?.disable_igloowork_custom_pin_notification}
            isAdmin={isAdmin}
            isLoading={loadingFields.has('disable_igloowork_custom_pin_notification')}
            handleToggleField={handleToggleField}
          />
          <UserNotificationToggleField
            label={t('user-panel:notifications.iglooLowBatteryNotifications')}
            id="disableIglooLowBatteryNotifications"
            name="disable_igloowork_low_battery_level_notification"
            value={!notificationsSettings?.disable_igloowork_low_battery_level_notification}
            isAdmin={isAdmin}
            isLoading={loadingFields.has('disable_igloowork_low_battery_level_notification')}
            handleToggleField={handleToggleField}
          />
        </div>
      </div>

      <div className="notification-section">
        <div className="notification-section__header">
          <Megaphone size={20} weight="regular" />
          <span className="notification-section__title">
            {t('user-panel:notifications.residentInspections')}
          </span>
        </div>
        <div className="notification-section__content">
          <UserNotificationToggleField
            label={t('user-panel:notifications.inspectionAssigned')}
            id="inspectionGetsAssigned"
            name="disable_checklist_assign"
            value={!notificationsSettings?.disable_checklist_assign}
            isAdmin={isAdmin}
            isLoading={false}
            handleToggleField={handleToggleField}
          />
          <UserNotificationToggleField
            label={t('user-panel:notifications.inspectionCloseToDueDate')}
            id="inspectionIsCloseToDueDate"
            name="disable_checklist_due_date_close"
            value={!notificationsSettings?.disable_checklist_due_date_close}
            isAdmin={isAdmin}
            isLoading={false}
            handleToggleField={handleToggleField}
          />
          <UserNotificationToggleField
            label={t('user-panel:notifications.inspectionOverdue')}
            id="inspectionIsOverdue"
            name="disable_checklist_overdue"
            value={!notificationsSettings?.disable_checklist_overdue}
            isAdmin={isAdmin}
            isLoading={false}
            handleToggleField={handleToggleField}
          />
          <UserNotificationToggleField
            label={t('user-panel:notifications.inspectionPendingApproval')}
            id="inspectionIsPendingApproval"
            name="disable_checklist_pending"
            value={!notificationsSettings?.disable_checklist_pending}
            isAdmin={isAdmin}
            isLoading={false}
            handleToggleField={handleToggleField}
          />
          <UserNotificationToggleField
            label={t('user-panel:notifications.inspectionCompleted')}
            id="inspectionHasBeenCompletedByResident"
            name="disable_checklist_revision"
            value={!notificationsSettings?.disable_checklist_revision}
            isAdmin={isAdmin}
            isLoading={false}
            handleToggleField={handleToggleField}
          />
        </div>
      </div>
    </div>
  )
}

UserNotifications.propTypes = {
  userData: PropTypes.object,
}

export default connect((state) => {
  return {
    userData: selectUserData(state),
  }
}, null)(UserNotifications)
